import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { formatPercentage, useMultiSelect, useSelect, useSwitch } from '@chilecompra/react-kit'
import { DataTable, MultiSelect, Select, Switch } from '@chilecompra/react-kit/components'
import {
  BUYERS_DISTRIBUTIONS_DICTIONARY,
  BUYERS_DISTRIBUTIONS_OPTIONS,
  BUYERS_DISTRIBUTIONS_TRANSLATION_DICTIONARY,
  OPTION_FILTER_SECTORS,
  OPTION_ALL
} from '../../config/settings/constants'
import { REACT_APP_MP_URL } from '../../config/settings/environment'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { formatAmount } from '../../modules/utils/formatters'
import generateYearsBetween from '../../modules/utils/years'
import { onGetBuyersTradedAmountsThunk } from './BuyersTradedAmountsSection.actions'
import LineChart from '../../components/Charts/LineChart.v2'
import TradedAmountsByYears from '../../components/Infographics/TradedAmountsByYears'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import { Description, GridContainer, GridItem, Link, Root, Title } from './BuyersTradedAmountsSection.styles'

/**
 * The BuyersTradedAmountsSection's container.
 */
const BuyersTradedAmountsSection = () => {
  const dispatch = useDispatch()
  const {
    endYear,
    latestUpdate,
    loading,
    modalities: mechanismOptions = [],
    sectors: sectorOptions = [],
    startYear
  } = useSelector(state => state.topNavbar)
  const { csv, data, loading: loadingData } = useSelector(state => state.buyersTradedAmountsSection)
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear,
    changeCallback: updatedValue =>
      dispatch(
        onGetBuyersTradedAmountsThunk({
          mechanismId: mechanismValue,
          sectorsIds: sectorsValues,
          year: updatedValue
        })
      ),
    overwriteCallback: () => endYear
  })
  const {
    values: sectorsValues,
    onChange: handleChangeSectors,
    onDelete: handleDeleteSectors
  } = useMultiSelect({
    initialValues: OPTION_FILTER_SECTORS,
    changeCallback: updatedValues =>
      dispatch(
        onGetBuyersTradedAmountsThunk({
          mechanismId: mechanismValue,
          sectorsIds: updatedValues,
          year: yearValue
        })
      ),
    formatCallbacks: [
      (updatedValues = []) => {
        const [firstValue] = updatedValues
        const indexOptionAll = OPTION_FILTER_SECTORS.indexOf(OPTION_ALL)
        if (
          updatedValues[0] === OPTION_FILTER_SECTORS[indexOptionAll + 1] &&
          OPTION_FILTER_SECTORS.length === updatedValues.length + 1
        ) {
          return [0]
        }
        if (OPTION_FILTER_SECTORS.length === updatedValues.length + 1 && !updatedValues.includes(OPTION_ALL)) {
          return OPTION_FILTER_SECTORS
        }
        if (updatedValues.includes(0) && updatedValues.length > 1) {
          const index = updatedValues.indexOf(0)
          updatedValues.splice(index, 1)
        }
        if (firstValue === OPTION_ALL && updatedValues.length === OPTION_FILTER_SECTORS.length - 1) {
          const index = updatedValues.indexOf(OPTION_ALL)
          updatedValues.splice(index, 1)
        }
        if (updatedValues.length !== OPTION_FILTER_SECTORS.length && updatedValues.length > 1) {
          updatedValues.sort((a, b) => a - b)
        }
        if (updatedValues.includes(OPTION_ALL)) {
          return OPTION_FILTER_SECTORS
        }
        if (updatedValues.length === 0) {
          return [0]
        }
        return updatedValues
      }
    ]
  })
  const { value: mechanismValue, onChange: handleChangeMechanism } = useSelect({
    initialValue: 7,
    changeCallback: updatedValue =>
      dispatch(
        onGetBuyersTradedAmountsThunk({
          mechanismId: updatedValue,
          sectorsIds: sectorsValues,
          year: yearValue
        })
      )
  })
  const { value: distributionValue, onChange: handleChangeDistribution } = useSelect({
    initialValue: BUYERS_DISTRIBUTIONS_DICTIONARY.amountMonthly
  })
  const { value: currencyValue, onChange: handleChangeCurrency } = useSwitch()
  const {
    disableCaptureImage,
    disableDownloadFile,
    ref: dataLayoutRef,
    onCaptureImage: handleCaptureImage,
    onChangeView: handleChangeView,
    onDownloadFile: handleDownloadFile
  } = useDataLayout({
    dependencies: [csv, yearValue],
    captureImageCallback: () =>
      exportToImage('buyers-traded-amounts-line-chart', `Info_Montos_mas_Transados_${yearValue}`),
    downloadFileCallback: () => exportToCsv(csv, `Info_Montos_mas_Transados_${yearValue}`)
  })
  const handleTooltipFormatter = useCallback(
    function () {
      const { amountMonthly, percentageMonthly, percentageYearly } = this.series.userOptions

      return `
        <ul>
          <li>
            - <strong>$${formatAmount(amountMonthly[this.point.index])}</strong> transados en ${this.x} de ${yearValue}.
          </li>
          <br />
          <li>
            - Corresponde al <strong>${formatPercentage(
              percentageYearly[this.point.index]
            )}</strong> del monto transado durante el año.
          </li>
          <br />
          <li>
            - Equivale al <strong>${formatPercentage(
              percentageMonthly[this.point.index]
            )}</strong> del total por el Estado durante ${this.x} de ${yearValue}.
          </li>
        </ul>
      `
    },
    [yearValue]
  )
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, startYear), [endYear, startYear])
  const enhancedData = useMemo(
    () =>
      data.map(datum => {
        const updateData = {
          ...datum,
          amountMonthly: currencyValue ? datum?.amountMonthlyUSD : datum?.amountMonthlyCLP,
          amountYearly: currencyValue ? datum?.amountYearlyUSD : datum?.amountYearlyCLP
        }

        return {
          ...updateData,
          data: updateData[BUYERS_DISTRIBUTIONS_TRANSLATION_DICTIONARY[distributionValue]]
        }
      }),
    [currencyValue, distributionValue, data]
  )
  const labels = useMemo(() => enhancedData.map(datum => ({ color: datum.color, name: datum.name })), [data])

  useEffect(() => {
    if (!endYear || loading) {
      return
    }

    dispatch(onGetBuyersTradedAmountsThunk({ mechanismId: mechanismValue, sectorsIds: sectorsValues, year: endYear }))
  }, [endYear, loading])

  return (
    <Root>
      <GridContainer justifyContent="center" padding="0 0 23px">
        <GridItem xs={12}>
          <Title>Montos transados durante el año</Title>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 28px">
        <GridItem xs={12}>
          <Description>
            Revisa el compartimiento y evolución de los montos transados de los sectores del Estado seleccionados.
            Recuerda que estos montos se extraen de la orden de compra que emite una entidad en{' '}
            <Link href={REACT_APP_MP_URL} rel="noreferrer" target="_blank">
              www.mercadopublico.cl
            </Link>
            , corresponde a compromisos de los organismos, no a pagos.
          </Description>
        </GridItem>
      </GridContainer>
      <DataLayout
        disableCaptureImage={disableCaptureImage}
        disableDownloadFile={disableDownloadFile}
        header={
          <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
            <GridItem md={8} xs={12}>
              <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
                <GridItem md={2} xs={12}>
                  <Select
                    label="AÑO"
                    loading={loading}
                    options={yearsOptions}
                    value={yearValue}
                    onChange={handleChangeYear}
                  />
                </GridItem>
                <GridItem md={3} xs={12}>
                  <MultiSelect
                    label="SECTOR"
                    loading={loading}
                    options={sectorOptions}
                    values={sectorsValues}
                    onChange={handleChangeSectors}
                    onDelete={handleDeleteSectors}
                  />
                </GridItem>
                <GridItem md={4} xs={12}>
                  <Select
                    label="MECANISMO DE COMPRA"
                    loading={loading}
                    options={mechanismOptions}
                    value={mechanismValue}
                    onChange={handleChangeMechanism}
                  />
                </GridItem>
                <GridItem md={3} xs={12}>
                  <Select
                    label="DISTRIBUCIÓN"
                    loading={loading}
                    options={BUYERS_DISTRIBUTIONS_OPTIONS}
                    value={distributionValue}
                    onChange={handleChangeDistribution}
                  />
                </GridItem>
              </GridContainer>
            </GridItem>
            <GridItem md={2} xs={12}>
              <Switch
                checked={currencyValue}
                color="primary"
                labelLeft="CLP"
                labelRight="USD"
                onChange={handleChangeCurrency}
                margin="0 0 0 32px"
              />
            </GridItem>
          </GridContainer>
        }
        infographic={<TradedAmountsByYears />}
        noData={!loadingData && data.length === 0}
        noDataText="No hay datos de organismos compradores para los filtros seleccionados."
        ref={dataLayoutRef}
        updatedDate={latestUpdate}
        onCaptureImage={handleCaptureImage}
        onChangeView={handleChangeView}
        onDownloadFile={handleDownloadFile}
      >
        <DataLayoutChild view={DataLayoutViews.CHART}>
          <GridContainer padding="0 0 1px">
            <GridItem xs={12}>
              <LineChart
                categories={[
                  'Enero',
                  'Febrero',
                  'Marzo',
                  'Abril',
                  'Mayo',
                  'Junio',
                  'Julio',
                  'Agosto',
                  'Septiembre',
                  'Octubre',
                  'Noviembre',
                  'Diciembre'
                ]}
                data={enhancedData}
                id="buyers-traded-amounts-line-chart"
                labels={labels}
                tooltipFormatter={handleTooltipFormatter}
              />
            </GridItem>
          </GridContainer>
        </DataLayoutChild>
        <DataLayoutChild view={DataLayoutViews.TABLE}>
          <GridContainer padding="0 0 20px">
            <GridItem xs={12}>
              <DataTable
                headers={[
                  { label: 'Año', value: 0 },
                  { label: 'Sector', value: 1 },
                  { label: 'Mes', value: 2 },
                  { label: 'Mecanismo de compra', value: 3 },
                  { label: 'Monto transado', value: 4 },
                  { label: 'Monto transado (USD)', value: 5 }
                ]}
                headersCellsProps={[
                  { width: '5%' },
                  { enableSort: true, width: '25%' },
                  { enableSort: true, width: '20%' },
                  { enableSort: true, width: '20%' },
                  { enableSort: true, width: '15%' },
                  { enableSort: true, width: '15%' }
                ]}
                loading={loading}
                records={csv.slice(1)}
                recordsDecorators={[
                  ([year]) => year,
                  ([, name]) => name,
                  ([, , month]) => month,
                  ([, , , mechanism]) => mechanism,
                  ([, , , , amount]) => `$${formatAmount(amount)}`,
                  ([, , , , , amountUSD]) => `USD ${formatAmount(amountUSD)}`
                ]}
              />
            </GridItem>
          </GridContainer>
        </DataLayoutChild>
      </DataLayout>
    </Root>
  )
}

export default BuyersTradedAmountsSection
